import React from 'react';
import { Users, Building2, Check, ArrowRight, Shield, Unlock, Sparkles } from 'lucide-react';

const BRAND = {
  turquoise: '#81CBD6',
  pink: '#F39DB2',
  yellow: '#FFD16F',
};

export const BenefitsSection = ({ theme, scrollToConfigurator }) => {
  const teamBenefits = [
    'Spürbar mehr Motivation & Teamgeist',
    'Gemeinsame Pausen stärken Zusammenhalt',
    'Wertschätzung die ankommt',
    'Highlight der Woche – jedes Mal aufs Neue',
  ];

  const companyBenefits = [
    'Null Verwaltungsaufwand – alles automatisch',
    'Planbare Kosten mit flexiblen Intervallen',
    'Steuerlich absetzbar als Betriebsausgabe',
    'Mitarbeiterbindung zum kleinen Preis',
  ];

  return (
    <section 
      data-section="benefits" 
      className="py-24 relative overflow-hidden" 
      style={{ backgroundColor: theme.bgSecondary }}
    >
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <span 
            className="inline-block mb-4 px-5 py-2 rounded-full text-sm font-semibold" 
            style={{ 
              background: `${BRAND.turquoise}15`, 
              border: `1px solid ${BRAND.turquoise}30`, 
              color: BRAND.turquoise 
            }}
          >
            Warum Fancy Donuts?
          </span>
          <h2 className="text-3xl md:text-4xl font-black">
            Vorteile für dein Team & Unternehmen
          </h2>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Für das Team */}
          <div 
            className="group p-6 rounded-2xl transition-all duration-300 hover:scale-[1.02]" 
            style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}
          >
            <div 
              className="step-icon-wrapper w-16 h-16 rounded-2xl flex items-center justify-center mb-5 transition-all duration-200 group-hover:scale-110" 
              style={{ backgroundColor: `${BRAND.pink}15` }}
            >
              <Users className="step-icon w-8 h-8 transition-all duration-200" style={{ color: BRAND.pink }} />
            </div>
            <h3 className="text-xl font-black mb-4">Für dein Team</h3>
            <ul className="space-y-4">
              {teamBenefits.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div 
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" 
                    style={{ backgroundColor: `${BRAND.pink}20` }}
                  >
                    <Check className="w-3 h-3" style={{ color: BRAND.pink }} />
                  </div>
                  <span className="text-sm" style={{ color: theme.textMuted }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Für das Unternehmen */}
          <div 
            className="group p-6 rounded-2xl transition-all duration-300 hover:scale-[1.02]" 
            style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}
          >
            <div 
              className="step-icon-wrapper w-16 h-16 rounded-2xl flex items-center justify-center mb-5 transition-all duration-200 group-hover:scale-110" 
              style={{ backgroundColor: `${BRAND.turquoise}15` }}
            >
              <Building2 className="step-icon w-8 h-8 transition-all duration-200" style={{ color: BRAND.turquoise }} />
            </div>
            <h3 className="text-xl font-black mb-4">Für dein Unternehmen</h3>
            <ul className="space-y-4">
              {companyBenefits.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div 
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" 
                    style={{ backgroundColor: `${BRAND.turquoise}20` }}
                  >
                    <Check className="w-3 h-3" style={{ color: BRAND.turquoise }} />
                  </div>
                  <span className="text-sm" style={{ color: theme.textMuted }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <button 
            onClick={() => scrollToConfigurator()}
            className="group px-10 py-4 rounded-full font-bold text-lg text-white transition-all hover:scale-105" 
            style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
          >
            Jetzt Abo konfigurieren 
            <ArrowRight className="inline w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export const TrustSection = ({ theme }) => {
  const trustItems = [
    { 
      Icon: Shield, 
      title: 'Zufriedenheitsgarantie', 
      desc: 'Nicht zufrieden? Geld zurück – ohne Wenn und Aber. Wir glauben an unsere Qualität.',
      color: BRAND.turquoise
    },
    { 
      Icon: Unlock, 
      title: 'Jederzeit kündbar', 
      desc: 'Kein Vertrag, keine Mindestlaufzeit. Du bleibst, weil du willst – nicht weil du musst.',
      color: BRAND.pink
    },
    { 
      Icon: Sparkles, 
      title: 'Premium Qualität', 
      desc: 'Täglich frisch, handgemacht mit besten Zutaten. 37 Sorten für jeden Geschmack.',
      color: BRAND.yellow
    },
  ];

  return (
    <section className="py-24" style={{ backgroundColor: theme.bg }}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-black">Garantiert sorgenfrei</h2>
          <p className="text-lg mt-4" style={{ color: theme.textMuted }}>
            Bei uns gehst du kein Risiko ein
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          {trustItems.map((item, i) => (
            <div 
              key={i} 
              className="group text-center p-6 rounded-2xl transition-all duration-300 hover:scale-[1.02]" 
              style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}
            >
              <div 
                className="step-icon-wrapper w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-5 transition-all duration-200 group-hover:scale-110" 
                style={{ backgroundColor: `${item.color}15` }}
              >
                <item.Icon className="step-icon w-8 h-8 transition-all duration-200" style={{ color: item.color }} />
              </div>
              <h3 className="text-lg font-black mb-3">{item.title}</h3>
              <p className="text-sm" style={{ color: theme.textMuted }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
